import { useEffect } from "react";
import { useBranding } from "../contexts/BrandingContext";

/**
 * DynamicFavicon — keeps the document <link rel="icon"> in sync with the
 * branding singleton. Uses the uploaded image URL when available, else
 * encodes the inline SVG as a data URL.
 */
export default function DynamicFavicon() {
  const { branding, loaded } = useBranding();

  useEffect(() => {
    if (!loaded) return;

    const fallbackToSvg = () => {
      if (!branding.logo_svg) return null;
      return {
        href: `data:image/svg+xml;utf8,${encodeURIComponent(branding.logo_svg.trim())}`,
        mime: "image/svg+xml",
      };
    };

    const apply = ({ href, mime }) => {
      if (!href) return;
      const head = document.head;
      Array.from(head.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")).forEach((el) =>
        el.parentNode?.removeChild(el)
      );
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = mime;
      link.href = href;
      head.appendChild(link);
      const apple = document.createElement("link");
      apple.rel = "apple-touch-icon";
      apple.href = href;
      head.appendChild(apple);
    };

    if (branding.use_image && branding.logo_url) {
      // Pre-flight check: if the URL fails to load, fall back to SVG
      const probe = new Image();
      probe.onload = () => {
        const ext = (branding.logo_url.split(".").pop() || "").toLowerCase().split("?")[0];
        const map = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", svg: "image/svg+xml", ico: "image/x-icon" };
        apply({ href: branding.logo_url, mime: map[ext] || "image/png" });
      };
      probe.onerror = () => {
        const fb = fallbackToSvg();
        if (fb) apply(fb);
      };
      probe.src = branding.logo_url;
    } else {
      const fb = fallbackToSvg();
      if (fb) apply(fb);
    }
  }, [loaded, branding.use_image, branding.logo_url, branding.logo_svg]);

  return null;
}
